import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Loader2,
  Users as UsersIcon,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const FIELD_LABEL =
  "text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block";
const INPUT_CLS = "h-10 bg-white border-black/10";

const BLANK = {
  role_id: "",
  name: "",
  email: "",
  phone: "",
  password: "",
  retail_outlet_id: "",
};

export default function Members() {
  const { can } = useAuth();
  const canAdd = can("member", "add");
  const canEdit = can("member", "edit");
  const canDelete = can("member", "delete");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [roles, setRoles] = useState([]);
  const [outlets, setOutlets] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, r, o] = await Promise.all([
        api.get("/members"),
        api.get("/roles"),
        api.get("/master/retail_outlets"),
      ]);
      setItems(m.data || []);
      setRoles(r.data || []);
      setOutlets(o.data || []);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load members"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (m) =>
        (m.name || "").toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q) ||
        (m.phone || "").toLowerCase().includes(q) ||
        (m.role_name || "").toLowerCase().includes(q) ||
        (m.retail_outlet_name || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const openCreate = () => {
    setForm(BLANK);
    setEditing(null);
    setShowPassword(false);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      role_id: row.role_id || "",
      name: row.name || "",
      email: row.email || "",
      phone: row.phone || "",
      password: "",
      retail_outlet_id: row.retail_outlet_id || "",
    });
    setEditing(row);
    setShowPassword(false);
    setDialogOpen(true);
  };

  const valid = () => {
    if (!form.role_id) return false;
    if (!form.name.trim()) return false;
    if (!form.email.trim()) return false;
    if (!form.phone.trim()) return false;
    if (!form.retail_outlet_id) return false;
    if (!editing && !form.password) return false;
    return true;
  };

  const handleSave = async () => {
    if (!valid()) {
      toast.error("Please fill all fields.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        const { data } = await api.put(`/members/${editing.id}`, payload);
        setItems((prev) =>
          prev.map((it) => (it.id === editing.id ? data : it))
        );
        toast.success("Member updated");
      } else {
        const { data } = await api.post("/members", form);
        setItems((prev) => [data, ...prev]);
        toast.success("Member created");
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/members/${deleteTarget.id}`);
      setItems((prev) => prev.filter((it) => it.id !== deleteTarget.id));
      toast.success("Member deleted");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="members-page">
      <header className="bg-white border-b border-black/5 px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[#0A0A0A] tracking-tight">
            Members
          </h1>
          <p className="text-xs text-[#525252] mt-0.5">
            Users attached to a role and retail outlet.
          </p>
        </div>
        {canAdd ? (
          <Button
            onClick={openCreate}
            className="bg-[#F50E02] hover:bg-[#D80C02] text-white h-10 rounded-md"
            data-testid="add-member-button"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Member
          </Button>
        ) : null}
      </header>

      <div className="p-8">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
            <Input
              placeholder="Search by name, email, phone, role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 bg-white border-black/10"
              data-testid="members-search-input"
            />
          </div>
          <span className="text-xs text-[#525252]">
            {filteredRows.length}{" "}
            {filteredRows.length === 1 ? "member" : "members"}
          </span>
        </div>

        <div className="bg-white border border-black/5 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="members-table">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-black/5 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Sr.No.</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Name</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Email</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Phone</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Role</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Retail Outlet</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252] text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-[#525252]">
                      <Loader2 className="h-5 w-5 animate-spin inline-block text-[#F50E02]" />
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-[#525252]">
                      <div className="inline-flex flex-col items-center gap-2">
                        <UsersIcon className="h-6 w-6 text-[#A3A3A3]" />
                        {search
                          ? <span>No members match &quot;{search}&quot;.</span>
                          : <span>No members yet. Click &quot;Add Member&quot; to create the first one.</span>
                        }
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((m, idx) => (
                    <tr
                      key={m.id}
                      className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]/60"
                      data-testid={`member-row-${m.id}`}
                    >
                      <td className="px-4 py-3 text-[#525252]">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium text-[#0A0A0A]">{m.name}</td>
                      <td className="px-4 py-3 text-[#0A0A0A]">{m.email}</td>
                      <td className="px-4 py-3 text-[#0A0A0A]">{m.phone || "—"}</td>
                      <td className="px-4 py-3 text-[#0A0A0A]">{m.role_name || "—"}</td>
                      <td className="px-4 py-3 text-[#0A0A0A]">{m.retail_outlet_name || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {canEdit ? (
                            <button
                              type="button"
                              onClick={() => openEdit(m)}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
                              aria-label="Edit"
                              data-testid={`member-edit-${m.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          ) : null}
                          {canDelete ? (
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(m)}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
                              aria-label="Delete"
                              data-testid={`member-delete-${m.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          ) : null}
                          {!canEdit && !canDelete ? (
                            <span className="text-[#A3A3A3] text-xs">—</span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add / Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Member" : "Add Member"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update the member. Leave password empty to keep the current one."
                : "All fields are required."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className={FIELD_LABEL}>
                Select Role <span className="text-[#F50E02]">*</span>
              </Label>
              <Select
                value={form.role_id}
                onValueChange={(v) => setField("role_id", v)}
              >
                <SelectTrigger className={INPUT_CLS} data-testid="member-role-select">
                  <SelectValue placeholder="Choose a role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-[#525252]">No roles available.</div>
                  ) : (
                    roles.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className={FIELD_LABEL}>
                Name <span className="text-[#F50E02]">*</span>
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="Enter name"
                className={INPUT_CLS}
                data-testid="member-name-input"
              />
            </div>

            <div>
              <Label className={FIELD_LABEL}>
                Email <span className="text-[#F50E02]">*</span>
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="Enter email"
                className={INPUT_CLS}
                data-testid="member-email-input"
              />
            </div>

            <div>
              <Label className={FIELD_LABEL}>
                Phone <span className="text-[#F50E02]">*</span>
              </Label>
              <Input
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                placeholder="Enter phone"
                className={INPUT_CLS}
                data-testid="member-phone-input"
              />
            </div>

            <div>
              <Label className={FIELD_LABEL}>
                Password {editing ? null : <span className="text-[#F50E02]">*</span>}
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setField("password", e.target.value)}
                  placeholder={editing ? "Leave blank to keep current" : "Enter password"}
                  className={`${INPUT_CLS} pr-10`}
                  data-testid="member-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#0A0A0A]"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className={FIELD_LABEL}>
                Select Retail Outlet <span className="text-[#F50E02]">*</span>
              </Label>
              <Select
                value={form.retail_outlet_id}
                onValueChange={(v) => setField("retail_outlet_id", v)}
              >
                <SelectTrigger className={INPUT_CLS} data-testid="member-outlet-select">
                  <SelectValue placeholder="Choose a retail outlet" />
                </SelectTrigger>
                <SelectContent>
                  {outlets.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-[#525252]">
                      No retail outlets. Add some in Master Data → Retail Outlet.
                    </div>
                  ) : (
                    outlets.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-black/10"
              data-testid="member-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !valid()}
              className="bg-[#F50E02] hover:bg-[#D80C02] text-white"
              data-testid="member-save"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editing ? "Update Member" : "Create Member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete member?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <b>{deleteTarget?.name}</b>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
              className="bg-[#F50E02] hover:bg-[#D80C02] text-white"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
